import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box
} from '@mui/material';
import { CompanyInfo } from '@/types/company-config';

interface CompanyInfoTabProps {
  data: CompanyInfo;
  onChange: (field: string, value: string) => void;
  readOnly?: boolean;
}

const CompanyInfoTab: React.FC<CompanyInfoTabProps> = ({ data, onChange, readOnly = false }) => {
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field, event.target.value);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Company Information
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Basic information about your company that will be displayed throughout the platform.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Company Name"
            value={data.name || ''}
            onChange={handleChange('name')}
            required
            disabled={readOnly}
            helperText="The display name for your company"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Legal Company Name"
            value={data.legalName || ''}
            onChange={handleChange('legalName')}
            required
            disabled={readOnly}
            helperText="The legal registered name of your company"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="EIN (Employer Identification Number)"
            value={data.ein || ''}
            onChange={handleChange('ein')}
            placeholder="XX-XXXXXXX"
            disabled={readOnly}
            helperText="Format: XX-XXXXXXX (optional)"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Industry"
            value={data.industry || ''}
            onChange={handleChange('industry')}
            disabled={readOnly}
            helperText="What industry is your company in?"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Website"
            value={data.website || ''}
            onChange={handleChange('website')}
            type="url"
            placeholder="https://www.example.com"
            disabled={readOnly}
            helperText="Your company's website URL"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Company Description"
            value={data.description || ''}
            onChange={handleChange('description')}
            multiline
            rows={4}
            disabled={readOnly}
            helperText="A brief description of your company and what you do"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Company Logo URL"
            value={data.logo || ''}
            onChange={handleChange('logo')}
            type="url"
            placeholder="https://www.example.com/logo.png"
            disabled={readOnly}
            helperText="URL to your company logo (will be displayed in the platform header)"
          />
          {data.logo && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Logo Preview:
              </Typography>
              <img
                src={data.logo}
                alt="Company Logo Preview"
                style={{
                  maxWidth: '200px',
                  maxHeight: '60px',
                  objectFit: 'contain',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '8px',
                  backgroundColor: '#fafafa'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyInfoTab;